// Frontend API calls to your Node.js backend for Facebook operations

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// "https://4ed11113e252.ngrok-free.app/api";

/**
 * Exchange short-lived token for long-lived token
 * @param {string} shortLivedToken - Short-lived access token
 * @returns {Promise<Object>} Long-lived token data
 */

const token = localStorage.getItem("authToken");

export const exchangeForLongLivedToken = async (
  shortLivedToken,
  userId = null
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/exchange-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ shortLivedToken, userId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to exchange token");
    }

    return await response.json();
  } catch (error) {
    console.error("Error exchanging token:", error);
    throw error;
  }
};

/**
 * Refresh long-lived token
 * @param {string} longLivedToken - Long-lived access token
 * @returns {Promise<Object>} Refreshed token data
 */
export const refreshLongLivedToken = async (longLivedToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/refresh-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ longLivedToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to refresh token");
    }

    return await response.json();
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
};

/**
 * Validate if access token is still valid
 * @param {string} accessToken - Facebook access token
 * @returns {Promise<Object>} Token validation result
 */
export const validateToken = async (accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/validate-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accessToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Token validation failed");
    }

    return await response.json();
  } catch (error) {
    console.error("Error validating token:", error);
    throw error;
  }
};

/**
 * Get comprehensive leads data with all related information
 * @param {string} accessToken - Facebook access token
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Comprehensive leads data
 */
export const getComprehensiveLeadsData = async (accessToken, options = {}) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/facebook/leads/comprehensive`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ accessToken, ...options }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || "Failed to fetch comprehensive leads data"
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching comprehensive leads data:", error);
    throw error;
  }
};

// ==================== ADDITIONAL FACEBOOK API FUNCTIONS ====================

/**
 * Get ad accounts
 * @param {string} accessToken - Facebook access token
 * @returns {Promise<Object>} Ad accounts data
 */
export const getAdAccounts = async (accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/ad-accounts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accessToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch ad accounts");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching ad accounts:", error);
    throw error;
  }
};

/**
 * Get Facebook pages
 * @param {string} accessToken - Facebook access token
 * @returns {Promise<Object>} Pages data
 */
export const getPages = async (accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/pages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accessToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch pages");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching pages:", error);
    throw error;
  }
};

/**
 * Get campaigns from ad account
 * @param {string} accessToken - Facebook access token
 * @param {string} adAccountId - Ad account ID
 * @returns {Promise<Object>} Campaigns data
 */
export const getCampaigns = async (accessToken, adAccountId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/campaigns`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accessToken, adAccountId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch campaigns");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching campaigns:", error);
    throw error;
  }
};

/**
 * Get leads from specific ad account
 * @param {string} accessToken - Facebook access token
 * @param {string} adAccountId - Ad account ID
 * @param {Object} options - Query options (limit, since, until)
 * @returns {Promise<Object>} Leads data
 */
export const getLeads = async (accessToken, adAccountId, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/leads`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accessToken, adAccountId, ...options }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch leads");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching leads:", error);
    throw error;
  }
};

/**
 * Get lead details
 * @param {string} accessToken - Facebook access token
 * @param {string} leadId - Lead ID
 * @returns {Promise<Object>} Lead details
 */
export const getLeadDetails = async (accessToken, leadId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/lead-details`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accessToken, leadId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch lead details");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching lead details:", error);
    throw error;
  }
};

// ==================== WEBHOOK API FUNCTIONS ====================

/**
 * Register user for webhook notifications
 * @param {Object} userData - User data for webhook registration
 * @returns {Promise<Object>} Registration response
 */
export const registerUser = async (userData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/facebook/user/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to register user for webhook");
    }

    return await response.json();
  } catch (error) {
    console.error("Error registering user for webhook:", error);
    throw error;
  }
};
